import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Button } from "@headerhawk/ui";
import { useAuthSession } from "@/components/providers/auth-context";
import { FullScreenLoader } from "@/components/ui/loader";
import {
  endImpersonation,
  enterDemoSession,
  markImpersonationEnded,
  readImpersonationState,
  type ImpersonationState,
} from "@/lib/auth/impersonation";
import { DESCOPE_PROJECT } from "@/lib/auth/descope-project";
import { getRefreshToken } from "@/lib/auth/token-manager";
import { loadPage } from "@/lib/demo/navigate";
import {
  DEMO_OPENING_LABEL,
  DEMO_STARTED_FLAG,
  signUpFromDemo,
} from "@/lib/demo/session";
import { startDemo, type DemoStartResult } from "@/lib/demo/start";

/**
 * The public demo (task 405), in a tab `bootImpersonation` marked as one.
 *
 * With no session yet it mints one and reloads onto the dashboard. With one,
 * it renders the app — whose banner, `DemoBanner`, lives inside the router —
 * and ends the session at its expiry. Once it has ended, or when the demo is
 * refused, it replaces the app with a page that offers **Start again** and
 * **Sign up free**, never the login screen: the member is ours, not the
 * visitor's.
 */
export function DemoGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ImpersonationState | null>(
    readImpersonationState,
  );
  if (state && state.ended === null && state.expiresAt === null) {
    return <DemoStarting next={state.next} />;
  }
  return (
    <DemoRunning state={state} onStateChange={setState}>
      {children}
    </DemoRunning>
  );
}

/**
 * One mint per page, however often React runs the effect: StrictMode runs
 * it twice in development, and a second mint would spend the address's daily
 * allowance for nothing.
 */
let starting: Promise<DemoStartResult> | null = null;

function DemoStarting({ next }: { next?: string }) {
  const [refused, setRefused] = useState<
    Extract<DemoStartResult, { ok: false }>["reason"] | null
  >(null);

  useEffect(() => {
    // `startDemo` answers every failure it knows of with a result; this
    // catch is for the one it does not, so no failure leaves a spinner.
    starting ??= startDemo().catch((): DemoStartResult => ({
      ok: false,
      reason: "unavailable",
    }));
    let current = true;
    void starting.then((result) => {
      if (!current) return;
      starting = null;
      if (!result.ok) {
        setRefused(result.reason);
        return;
      }
      enterDemoSession(result.refreshJwt, result.expiresAt);
      try {
        window.sessionStorage.setItem(DEMO_STARTED_FLAG, "1");
      } catch {
        // Only the event is lost.
      }
      loadPage(next ?? "/dashboard", { replace: true });
    });
    return () => {
      current = false;
    };
  }, [next]);

  if (refused) return <DemoRefused reason={refused} next={next} />;
  return <FullScreenLoader label={DEMO_OPENING_LABEL} />;
}

function DemoRunning({
  state,
  onStateChange,
  children,
}: {
  state: ImpersonationState | null;
  onStateChange: (state: ImpersonationState | null) => void;
  children: ReactNode;
}) {
  const session = useAuthSession();
  const expiry = state?.expiresAt ? Date.parse(state.expiresAt) : NaN;
  const live = state !== null && state.ended === null;

  const end = useCallback(
    async (reason: "expired" | "ended") => {
      await endImpersonation(DESCOPE_PROJECT, reason);
      onStateChange(readImpersonationState());
    },
    [onStateChange],
  );

  useEffect(() => {
    if (!live) return;
    const remaining = expiry - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) {
      void end("expired");
      return;
    }
    const timer = setTimeout(() => void end("expired"), remaining);
    return () => clearTimeout(timer);
  }, [live, expiry, end]);

  // As for an operator's view: a refused refresh clears the token, and that
  // session is over; a failed one leaves it, and a reload may recover.
  const unauthenticated =
    live && !session.isSessionLoading && !session.isAuthenticated;
  const refused = unauthenticated && getRefreshToken() === null;
  useEffect(() => {
    if (refused) {
      markImpersonationEnded("ended");
      onStateChange(readImpersonationState());
    }
  }, [refused, onStateChange]);

  if (!state || state.ended !== null || refused) {
    return <DemoEnded reason={state?.ended ?? "ended"} next={state?.next} />;
  }
  if (unauthenticated) {
    return <DemoRefused reason="unavailable" next={state.next} />;
  }
  return <>{children}</>;
}

/** A new demo, on the screen the first one was opened on. */
function startAgain(next?: string): void {
  loadPage(next ? `/demo?to=${encodeURIComponent(next)}` : "/demo");
}

function DemoPage({
  title,
  children,
  actions,
}: {
  title: string;
  children: ReactNode;
  actions: ReactNode;
}) {
  return (
    <main
      className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center"
      data-demo-page=""
    >
      <h1 className="text-xl font-semibold">{title}</h1>
      <div className="max-w-md space-y-2 text-muted-foreground">{children}</div>
      <div className="mt-2 flex flex-wrap justify-center gap-2">{actions}</div>
    </main>
  );
}

function SignUpButton({ location }: { location: string }) {
  const [leaving, setLeaving] = useState(false);
  return (
    <Button
      type="button"
      disabled={leaving}
      onClick={() => {
        setLeaving(true);
        void signUpFromDemo(location);
      }}
    >
      Sign up free
    </Button>
  );
}

function DemoEnded({
  reason,
  next,
}: {
  reason: NonNullable<ImpersonationState["ended"]>;
  next?: string;
}) {
  return (
    <DemoPage
      title="The demo has ended"
      actions={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => startAgain(next)}
          >
            Start again
          </Button>
          <SignUpButton location="demo_ended" />
        </>
      }
    >
      <p>
        {reason === "expired"
          ? "A demo session lasts an hour, and this one is over."
          : "This demo session is over."}{" "}
        Nothing you did in it was saved.
      </p>
      <p className="text-sm">
        Start again for a new session, or sign up free to see your own site.
      </p>
    </DemoPage>
  );
}

const REFUSED_COPY: Record<
  Extract<DemoStartResult, { ok: false }>["reason"],
  { title: string; body: string; retry: boolean }
> = {
  not_configured: {
    title: "The demo is not available here",
    body: "There is no demo organization in this environment.",
    retry: false,
  },
  too_many: {
    title: "No more demos today from this address",
    body: "This network has started as many demo sessions as a day allows. Sign up free to use HeaderHawk on your own site, or come back tomorrow.",
    retry: false,
  },
  unavailable: {
    title: "The demo could not start",
    body: "The service that starts demo sessions did not answer. Try again in a moment.",
    retry: true,
  },
};

function DemoRefused({
  reason,
  next,
}: {
  reason: Extract<DemoStartResult, { ok: false }>["reason"];
  next?: string;
}) {
  const copy = REFUSED_COPY[reason];
  return (
    <DemoPage
      title={copy.title}
      actions={
        <>
          {copy.retry && (
            <Button
              type="button"
              variant="outline"
              onClick={() => startAgain(next)}
            >
              Try again
            </Button>
          )}
          <SignUpButton location="demo_refused" />
        </>
      }
    >
      <p data-demo-refused={reason}>{copy.body}</p>
    </DemoPage>
  );
}
